/**
 * Hook for handling RFP submission and email draft
 */

import { extractErrorMessage, useApi } from "@platform/api";
import { createMailtoLink } from "@platform/fe-utils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";

import { queryKeys } from "../api";

import {
  formatCarrierDueDate,
  formatEffectiveDate,
} from "./useReviewSectionState";

import type {
  SendRfpCarrier,
  SendRfpSubmissionData,
  SubmissionStatus,
} from "../api";
import type { OfflineSubmissionFormData } from "../OfflineSubmissionForm";

/**
 * Serializes a date picked from the calendar as midday UTC on that calendar day.
 *
 * The value lands in rfp_submissions.submitted_at, a timestamp read back by two
 * consumers that disagree about zones: reporting treats it as a UTC date, while
 * the carrier tracker renders it in the viewer's local zone. Midnight satisfies
 * only one of them at a time — 00:00Z reads as the previous day anywhere west of
 * UTC, local midnight as the previous day for reporting anywhere east of it.
 * Noon satisfies both from UTC-12 through UTC+11, which covers every zone the
 * product is used in; a viewer at UTC+12 or beyond (New Zealand, Kiribati) would
 * still see the following day.
 */
const NOON = 12;

const toUtcDateOnly = (date: Date): string =>
  new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), NOON),
  ).toISOString();

interface RfpSubmissionEmailParams {
  brokerName?: string;
  clientName: string;
  effectiveDate: string;
  numberOfEmployees: number;
  carrierDueDate: string;
}

/**
 * Builds the RFP submission email content
 */
export function buildRfpSubmissionEmail({
  brokerName,
  clientName,
  effectiveDate,
  numberOfEmployees,
  carrierDueDate,
}: RfpSubmissionEmailParams): { subject: string; body: string } {
  const brokerTemplate = brokerName ? `Broker: ${brokerName} / ` : "";
  const subject = `${brokerTemplate}RFP: ${clientName} - ${numberOfEmployees} lives - DUE: ${carrierDueDate || ""}`;

  const body = `Hello,

Attached please find the RFP and supporting documents to propose benefits for ${clientName}, effective ${effectiveDate}.

Please match current benefits unless otherwise noted on the RFP. If there are any deviations or enhancements, please clearly outline them in your email or offer.

Report or additional information Required--

If you have questions, please let me know.

Thank you for your consideration! We look forward to seeing your quote!`;

  return { subject, body };
}

interface UseSendRfpSubmissionParams {
  ecoRfpId: string;
  brokerPortalRfpId: string;
  carriers: SendRfpCarrier[];
  selectedCarriers: string[];
  selectedCoverageTypes: string[];
  selectedCoveragePlanTypes?: Record<string, string[]>;
  selectedUnconnectedCarriers: SendRfpCarrier[];
  selectedConnectedCarriers: SendRfpCarrier[];
  isOfflineSubmission: boolean;
  submissionData: SendRfpSubmissionData | undefined;
  getOfflineFormValues?: () => OfflineSubmissionFormData;
}

interface UseSendRfpSubmissionResult {
  submissionStatus: SubmissionStatus;
  submissionError: string | null;
  submissionVersion: number | null;
  beginSubmission: () => void;
  failSubmission: (error: string) => void;
  handleSubmit: () => void;
}

/**
 * Handles RFP submission and email draft creation
 */
export function useSendRfpSubmission({
  ecoRfpId,
  brokerPortalRfpId,
  carriers,
  selectedCarriers,
  selectedCoverageTypes,
  selectedCoveragePlanTypes = {},
  selectedUnconnectedCarriers,
  selectedConnectedCarriers,
  isOfflineSubmission,
  submissionData,
  getOfflineFormValues,
}: UseSendRfpSubmissionParams): UseSendRfpSubmissionResult {
  const axiosInstance = useApi();
  const queryClient = useQueryClient();
  const [submissionStatus, setSubmissionStatus] =
    useState<SubmissionStatus>("idle");
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [submissionVersion, setSubmissionVersion] = useState<number | null>(
    null,
  );

  /** Set status to "submitting" without triggering any API call. */
  const beginSubmission = useCallback((): void => {
    setSubmissionStatus("submitting");
    setSubmissionError(null);
  }, []);

  /** Transition to the error state when a pre-submit step fails. */
  const failSubmission = useCallback((error: string): void => {
    setSubmissionError(error);
    setSubmissionStatus("error");
  }, []);

  // Launch one email draft per carrier that needs manual email submission.
  // - Unconnected carriers always need a manual email.
  // - Connected carriers need a manual email when offline submission is toggled on.
  const launchEmailDrafts = useCallback((): void => {
    if (!submissionData) return;

    const carriersToEmail = isOfflineSubmission
      ? [...selectedUnconnectedCarriers, ...selectedConnectedCarriers]
      : selectedUnconnectedCarriers;

    if (carriersToEmail.length === 0) return;

    // Build email subject/body from submission data (format dates for display)
    const { subject, body } = buildRfpSubmissionEmail({
      brokerName: submissionData.brokerName ?? undefined,
      clientName: submissionData.clientName,
      effectiveDate: formatEffectiveDate(submissionData.effectiveDate),
      numberOfEmployees: submissionData.numberOfEmployees,
      carrierDueDate: formatCarrierDueDate(
        submissionData.carrierDueDate ?? undefined,
      ),
    });

    // CC the primary analyst if available
    const baseCc: string[] = [];
    if (submissionData.primaryAnalystEmail) {
      baseCc.push(submissionData.primaryAnalystEmail);
    }

    for (const carrier of carriersToEmail) {
      if (!carrier.salesRepEmail) continue;
      const cc = carrier.supportEmail
        ? [...baseCc, carrier.supportEmail]
        : baseCc;
      const emailUrl = createMailtoLink({
        to: [carrier.salesRepEmail],
        cc,
        subject,
        body,
      });
      window.open(emailUrl, "_blank", "noopener,noreferrer");
    }
  }, [
    selectedUnconnectedCarriers,
    selectedConnectedCarriers,
    isOfflineSubmission,
    submissionData,
  ]);

  const onMutationSuccess = useCallback(
    (response: { data: { version?: number } }): void => {
      launchEmailDrafts();
      setSubmissionVersion(response.data.version ?? null);
      setSubmissionStatus("success");
      void queryClient.invalidateQueries({
        queryKey: queryKeys.rfpCarriers(brokerPortalRfpId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.rfpWorkbenchSend(brokerPortalRfpId),
      });
      // Refetch prepareRfpDetails to update RFP data
      void queryClient.invalidateQueries({
        queryKey: ["prepareRfpDetails", brokerPortalRfpId],
      });
    },
    [launchEmailDrafts, queryClient, brokerPortalRfpId],
  );

  const onMutationError = useCallback((error: Error): void => {
    setSubmissionError(extractErrorMessage(error));
    setSubmissionStatus("error");
  }, []);

  // Submit RFP mutation — submits all selected carriers (connected + unconnected) via API.
  // carrier-rfpq uses credential checks to classify carriers as api or email and dispatches
  // the appropriate workflow (RFPOrchestratorWorkflow or EmailCarrierNotificationWorkflow).
  const submitRfpMutation = useMutation({
    mutationFn: async () => {
      const selectedCarrierObjects = carriers.filter((c) =>
        selectedCarriers.includes(c.id),
      );
      const shareIdentifiers = selectedCarrierObjects
        .map((c) => c.shareIdentifier)
        .filter((id): id is string => !!id);

      if (shareIdentifiers.length === 0) {
        throw new Error("No valid carriers selected for submission");
      }

      return axiosInstance.post(`btx/v1/rfps/${ecoRfpId}/submit`, {
        carriers: shareIdentifiers,
        coverages: selectedCoverageTypes,
        coveragePlanTypes: selectedCoveragePlanTypes,
      });
    },
    onSuccess: onMutationSuccess,
    onError: onMutationError,
  });

  // Offline submission mutation
  const submitOfflineMutation = useMutation({
    mutationFn: async () => {
      const offlineData = getOfflineFormValues?.();
      const shareIdentifiers = carriers
        .filter((c) => selectedCarriers.includes(c.id) && !!c.shareIdentifier)
        .map((c) => c.shareIdentifier)
        .filter((id): id is string => !!id);

      if (!offlineData || shareIdentifiers.length === 0) {
        throw new Error("Missing offline form data or carriers");
      }

      return axiosInstance.post(`btx/v1/rfps/${ecoRfpId}/submit`, {
        carriers: shareIdentifiers,
        coverages: selectedCoverageTypes,
        coveragePlanTypes: selectedCoveragePlanTypes,
        offline: true,
        offlineReason: offlineData.offlineReason,
        offlineDescription: offlineData.offlineDescription,
        dateSentToCarriers: toUtcDateOnly(offlineData.dateSentToCarriers),
        // Form state keeps the 0-99 entries as validated numeric strings; the
        // API takes integers keyed by coverage type.
        offlinePlansQuoted: Object.fromEntries(
          Object.entries(offlineData.offlinePlansQuoted).map(
            ([coverageType, plansQuoted]) => [
              coverageType,
              Number(plansQuoted),
            ],
          ),
        ),
      });
    },
    onSuccess: onMutationSuccess,
    onError: onMutationError,
  });

  const handleSubmit = useCallback((): void => {
    setSubmissionStatus("submitting");
    setSubmissionError(null);

    if (isOfflineSubmission) {
      submitOfflineMutation.mutate();
      return;
    }

    const hasConnectedSelected = selectedConnectedCarriers.length > 0;
    const hasUnconnectedSelected = selectedUnconnectedCarriers.length > 0;

    if (hasConnectedSelected || hasUnconnectedSelected) {
      // Always submit via API so carrier-rfpq creates submissions for all carriers.
      // Connected carriers get the RFPOrchestratorWorkflow; unconnected carriers get
      // the EmailCarrierNotificationWorkflow (carrier-rfpq classifies by credentials).
      // Email drafts are launched on success via onMutationSuccess → launchEmailDrafts.
      submitRfpMutation.mutate();
    } else {
      setSubmissionStatus("idle");
      setSubmissionError("No carriers selected for submission");
    }
  }, [
    isOfflineSubmission,
    selectedConnectedCarriers,
    selectedUnconnectedCarriers,
    submitRfpMutation,
    submitOfflineMutation,
  ]);

  return {
    submissionStatus,
    submissionError,
    submissionVersion,
    beginSubmission,
    failSubmission,
    handleSubmit,
  };
}
